import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiShare2 } from "react-icons/fi";
import { AppIcon, APP } from "../components/mobileUi";
import {
  DESK,
  DesktopButton,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../components/desktopUi";
import { appImages } from "../assets/appicons";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { EMP_BASE_PATH, EMP_API_BASE_URL } from "../config";
import { getMyAffiliateCodeApi } from "../api/auth.api";
import { buildAffiliateLink, shareOrCopyLink } from "../../utils/affiliate";

const SUPPORT_DEPARTMENT_CODES = [
  "PHONG_HANH_CHINH_NHAN_SU",
  "PHONG_KE_TOAN",
  "HO_TRO_BAN_HANG_CRM",
];

const Section = ({ title, children }) => (
  <Box mt="18px">
    <Text fontSize="16px" fontWeight={600} color={APP.black} mb="10px">
      {title}
    </Text>
    <Box bg={APP.surface} border="1px solid" borderColor={APP.cardBorder} borderRadius="12px" overflow="hidden">
      {children}
    </Box>
  </Box>
);

const RowItem = ({ icon, title, onClick, last }) => (
  <Flex as="button" type="button" onClick={onClick} align="center" justify="space-between" w="100%" px="14px" py="14px">
    <Flex align="center" gap="10px">
      <AppIcon src={icon} size={24} color={APP.tabActive} />
      <Text fontSize="14px" color={APP.black} textAlign="left">
        {title}
      </Text>
    </Flex>
    <AppIcon src={appImages.icon_chevron_right} size={18} color="#94A3B8" />
  </Flex>
);

const Divider = () => <Box h="1px" bg={APP.divider} ml="48px" mr="14px" />;

// Thẻ chức năng của bản web (đồng bộ với lưới chức năng ở trang chủ desktop)
const DeskTile = ({ icon, label, onClick }) => (
  <Flex
    as="button"
    type="button"
    onClick={onClick}
    align="center"
    gap="12px"
    p="12px"
    borderRadius="10px"
    border="1px solid"
    borderColor={DESK.border}
    bg={DESK.surface}
    _hover={{ borderColor: DESK.primary, boxShadow: "0 2px 10px rgba(8,20,45,0.08)" }}
    transition="all .15s"
    textAlign="left"
  >
    <Flex
      w="44px"
      h="44px"
      flexShrink={0}
      borderRadius="10px"
      bg="var(--fill-avatar)"
      align="center"
      justify="center"
    >
      <AppIcon src={icon} size={22} color={APP.tabActive} />
    </Flex>
    <Text fontSize="14px" fontWeight={600} color={DESK.info} whiteSpace="normal">
      {label}
    </Text>
  </Flex>
);

const AccountLanding = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { user, logout } = useEmployeeAuth();
  const [confirm, setConfirm] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState("");

  // Mã giới thiệu của chính nhân viên, dùng để dựng link affiliate
  useEffect(() => {
    let active = true;

    getMyAffiliateCodeApi()
      .then((data) => {
        if (active) setAffiliateCode(data?.affiliateCode || "");
      })
      .catch(() => {
        /* không có mã thì ẩn dòng link, không cần báo lỗi */
      });

    return () => {
      active = false;
    };
  }, []);

  const affiliateLink = buildAffiliateLink(affiliateCode);

  // Mở cửa sổ chia sẻ của máy (giống nút share trên app), máy nào không hỗ trợ
  // thì tự động copy link vào clipboard
  const onShareAffiliateLink = async () => {
    // Chia sẻ thì kèm câu giới thiệu; còn nhánh fallback (trình duyệt không hỗ
    // trợ chia sẻ) chỉ copy đúng đường dẫn
    const result = await shareOrCopyLink(affiliateLink, {
      title: "Link giới thiệu MSH Group",
      text: "Đăng ký làm nhân viên MSH Group qua link giới thiệu của tôi",
    });

    if (result === "copied") toast.success("Đã copy link giới thiệu");
    if (result === "failed") {
      toast.error("Không chia sẻ được link, vui lòng copy thủ công");
    }
  };

  const isManager = (user?.jobPosition?.code || "") !== "NHAN_VIEN";
  const isSupport = SUPPORT_DEPARTMENT_CODES.includes(user?.supportDepartment?.code || "");
  const showApproval = isManager || isSupport;

  const avatarUri = useMemo(() => {
    const raw = user?.avatar;
    if (!raw) return appImages.empty_avatar;
    if (raw.startsWith("http")) return raw;
    const base = EMP_API_BASE_URL.replace(/\/+$/, "");
    const path = raw.startsWith("/") ? raw : `/${raw}`;
    return `${base}${path}`;
  }, [user?.avatar]);

  const go = (to) => () => navigate(to);
  // Chức năng chưa clone -> trang bảo trì
  const noop = () => navigate(`${EMP_BASE_PATH}/bao-tri`);

  const doLogout = () => {
    logout();
    navigate(`${EMP_BASE_PATH}/login`, { replace: true });
  };

  // Nút chia sẻ link giới thiệu, đặt cạnh chip chức vụ trên header
  const affiliateShareButton = (onDark) =>
    affiliateLink ? (
      <Flex
        as="button"
        type="button"
        onClick={onShareAffiliateLink}
        aria-label="Chia sẻ link giới thiệu"
        title="Chia sẻ link giới thiệu"
        w="30px"
        h="30px"
        flexShrink={0}
        align="center"
        justify="center"
        borderRadius="full"
        bg={onDark ? "rgba(255,255,255,0.22)" : "#EEF5FF"}
        _hover={{ opacity: 0.85 }}
      >
        <FiShare2 size={16} color={onDark ? "#FFFFFF" : APP.primary100} />
      </Flex>
    ) : null;

  // Hộp xác nhận đăng xuất - dùng chung cho cả hai bản
  const logoutConfirm = confirm ? (
    <Flex position="fixed" inset="0" bg="rgba(0,0,0,0.45)" align="center" justify="center" px="20px" zIndex={50} onClick={() => setConfirm(false)}>
      <Box bg="white" borderRadius="18px" p="16px" w="100%" maxW="360px" onClick={(e) => e.stopPropagation()}>
        <Text fontSize="17px" fontWeight={700} color={APP.black}>Đăng xuất</Text>
        <Text fontSize="14px" color={APP.black} mt="8px">Bạn chắc chắn muốn đăng xuất?</Text>
        <Flex gap="10px" mt="16px">
          <Flex as="button" flex="1" h="44px" borderRadius="8px" bg="#EEF5FF" align="center" justify="center" onClick={() => setConfirm(false)}>
            <Text fontSize="14px" fontWeight={600} color={APP.black}>Huỷ</Text>
          </Flex>
          <Flex as="button" flex="1" h="44px" borderRadius="8px" bg="#E5484D" align="center" justify="center" onClick={doLogout}>
            <Text fontSize="14px" fontWeight={600} color="white">Đăng xuất</Text>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  ) : null;

  // ===================== Bản WEB (desktop) =====================
  // Cùng danh sách chức năng, cùng handler; chỉ đổi cách trình bày: thẻ hồ sơ
  // nằm ngang + lưới thẻ chức năng thay cho danh sách dòng kiểu app.
  if (isDesktop) {
    const sections = [
      {
        title: "Hợp đồng dịch vụ",
        items: [
          { key: "hdnv", icon: appImages.icon_HDNV, label: "Hợp đồng nhân viên", onClick: noop },
          {
            key: "chu-ky",
            icon: appImages.icon_QL_chu_ky,
            label: "Quản lý chữ ký",
            onClick: go(`${EMP_BASE_PATH}/chu-ky`),
          },
        ],
      },
      {
        title: "Chuyên cần",
        items: [
          {
            key: "cham-cong",
            icon: appImages.icon_cham_cong,
            label: "Chấm công",
            onClick: go(`${EMP_BASE_PATH}/chuyen-can`),
          },
          {
            key: "cc-ngoai",
            icon: appImages.icon_dky_cn,
            label: "Đăng ký chấm công ngoài",
            onClick: go(`${EMP_BASE_PATH}/cham-cong-ngoai`),
          },
          {
            key: "lam-bu",
            icon: appImages.icon_dk_lam_bu,
            label: "Đăng ký làm bù",
            onClick: go(`${EMP_BASE_PATH}/lam-bu`),
          },
          {
            key: "nghi-phep",
            icon: appImages.icon_xin_nghi_phep,
            label: "Xin nghỉ phép",
            onClick: go(`${EMP_BASE_PATH}/nghi-phep`),
          },
          ...(showApproval
            ? [
                {
                  key: "pd-cc-ngoai",
                  icon: appImages.icon_phe_duyet_cong_ngoai,
                  label: "Phê duyệt chấm công ngoài",
                  onClick: noop,
                },
                {
                  key: "pd-nghi-phep",
                  icon: appImages.icon_phe_duyet_np,
                  label: "Phê duyệt nghỉ phép",
                  onClick: noop,
                },
              ]
            : []),
        ],
      },
      {
        title: "Tiện ích tài chính",
        items: [
          {
            key: "thu-nhap",
            icon: appImages.icon_Thu_nhap_ca_nhan,
            label: "Thu nhập cá nhân",
            onClick: go(`${EMP_BASE_PATH}/tai-chinh/thu-nhap`),
          },
        ],
      },
      {
        title: "Cài đặt",
        items: [
          {
            key: "thiet-lap",
            icon: appImages.icon_Cai_dat,
            label: "Thiết lập tài khoản",
            onClick: go(`${EMP_BASE_PATH}/tai-khoan/thong-tin`),
          },
          { key: "dksd", icon: appImages.icon_DKSD, label: "Điều khoản sử dụng", onClick: noop },
        ],
      },
    ];

    return (
      <DesktopPage title="Tài khoản">
        <DesktopPanel>
          <Flex align="center" gap="20px" flexWrap="wrap">
            <Box
              w="88px"
              h="88px"
              borderRadius="full"
              bg="#DCE7F7"
              backgroundImage={`url(${avatarUri})`}
              backgroundSize="cover"
              backgroundPosition="center"
              flexShrink={0}
            />
            <Box flex="1" minW="220px">
              <Text fontSize="20px" fontWeight={600} color={DESK.title}>
                {user?.fullName || "Nhân viên"}
              </Text>
              <Flex align="center" gap="10px" mt="6px" flexWrap="wrap">
                <Text fontSize="14px" fontWeight={500} color={DESK.label}>
                  {user?.code || ""}
                </Text>
                {user?.jobPosition?.name && (
                  <Box bg="#EEF5FF" borderRadius="100px" px="12px" py="4px">
                    <Text fontSize="12px" fontWeight={600} color={APP.primary100}>
                      {user.jobPosition.name}
                    </Text>
                  </Box>
                )}
                {affiliateShareButton(false)}
              </Flex>
              <Text fontSize="13px" color={DESK.label} mt="8px">
                Bạn đang dùng phiên bản Web
              </Text>
            </Box>
            <DesktopButton
              variant="danger"
              onClick={() => setConfirm(true)}
              icon={<AppIcon src={appImages.icon_logout} size={18} color="#FFFFFF" />}
            >
              Đăng xuất
            </DesktopButton>
          </Flex>
        </DesktopPanel>

        {sections.map((section) => (
          <DesktopPanel key={section.title}>
            <Text fontSize="16px" fontWeight={600} color={DESK.title}>
              {section.title}
            </Text>
            <Box
              mt="16px"
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(260px, 1fr))"
              gap="16px"
            >
              {section.items.map((item) => (
                <DeskTile
                  key={item.key}
                  icon={item.icon}
                  label={item.label}
                  onClick={item.onClick}
                />
              ))}
            </Box>
          </DesktopPanel>
        ))}

        {logoutConfirm}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg={APP.surface} minH="100%">
      {/* Header xanh */}
      <Box
        backgroundImage={`url(${appImages.background_info})`}
        backgroundSize="cover"
        backgroundPosition="center"
        bgColor={APP.primary100}
        pt="36px"
        pb="34px"
        px="20px"
      >
        <Flex direction="column" align="center" gap="10px">
          <Box
            w="64px"
            h="64px"
            borderRadius="full"
            bg="#DCE7F7"
            backgroundImage={`url(${avatarUri})`}
            backgroundSize="cover"
            backgroundPosition="center"
          />
          <Text fontSize="16px" fontWeight={600} color="white">
            {user?.fullName || "Nhân viên"}
          </Text>
          <Flex align="center" gap="10px">
            <Text fontSize="14px" fontWeight={600} color="white">
              {user?.code || ""}
            </Text>
            {user?.jobPosition?.name && (
              <Box bg="white" borderRadius="100px" px="12px" py="5px">
                <Text fontSize="12px" fontWeight={600} color={APP.primary100}>
                  {user.jobPosition.name}
                </Text>
              </Box>
            )}
            {affiliateShareButton(true)}
          </Flex>
        </Flex>
      </Box>

      {/* Tấm trắng bo góc */}
      <Box mt="-18px" bg={APP.surface} borderTopRadius="24px" pt="18px" px="20px" pb="90px">
        {/* Version */}
        <Box bg="#EEF5FF" borderRadius="18px" p="14px">
          <Text fontSize="13px" color={APP.black} textAlign="center">
            Bạn đang dùng phiên bản Web
          </Text>
        </Box>

        <Section title="Hợp đồng dịch vụ">
          <RowItem icon={appImages.icon_HDNV} title="Hợp đồng nhân viên" onClick={noop} />
          <Divider />
          <RowItem icon={appImages.icon_QL_chu_ky} title="Quản lý chữ ký" onClick={go(`${EMP_BASE_PATH}/chu-ky`)} />
        </Section>

        <Section title="Chuyên cần">
          <RowItem icon={appImages.icon_cham_cong} title="Chấm công" onClick={go(`${EMP_BASE_PATH}/chuyen-can`)} />
          <Divider />
          <RowItem icon={appImages.icon_dky_cn} title="Đăng ký chấm công ngoài" onClick={go(`${EMP_BASE_PATH}/cham-cong-ngoai`)} />
          <Divider />
          <RowItem icon={appImages.icon_dk_lam_bu} title="Đăng ký làm bù" onClick={go(`${EMP_BASE_PATH}/lam-bu`)} />
          <Divider />
          <RowItem icon={appImages.icon_xin_nghi_phep} title="Xin nghỉ phép" onClick={go(`${EMP_BASE_PATH}/nghi-phep`)} />
          {showApproval && (
            <>
              <Divider />
              <RowItem icon={appImages.icon_phe_duyet_cong_ngoai} title="Phê duyệt chấm công ngoài" onClick={noop} />
              <Divider />
              <RowItem icon={appImages.icon_phe_duyet_np} title="Phê duyệt nghỉ phép" onClick={noop} />
            </>
          )}
        </Section>

        <Section title="Tiện ích tài chính">
          <RowItem icon={appImages.icon_Thu_nhap_ca_nhan} title="Thu nhập cá nhân" onClick={go(`${EMP_BASE_PATH}/tai-chinh/thu-nhap`)} />
        </Section>

        <Section title="Cài đặt">
          <RowItem icon={appImages.icon_Cai_dat} title="Thiết lập tài khoản" onClick={go(`${EMP_BASE_PATH}/tai-khoan/thong-tin`)} />
          <Divider />
          <RowItem icon={appImages.icon_DKSD} title="Điều khoản sử dụng" onClick={noop} />
        </Section>

        {/* Logout */}
        <Flex
          as="button"
          type="button"
          onClick={() => setConfirm(true)}
          mt="18px"
          w="100%"
          bg="#EEF5FF"
          borderRadius="16px"
          py="16px"
          align="center"
          justify="center"
          gap="10px"
        >
          <AppIcon src={appImages.icon_logout} size={24} color={APP.primary100} />
          <Text fontSize="14px" fontWeight={700} color={APP.primary100}>
            ĐĂNG XUẤT
          </Text>
        </Flex>
      </Box>

      {/* Confirm đăng xuất */}
      {logoutConfirm}
    </Box>
  );
};

export default AccountLanding;
